import { WellsFargoTransaction } from '@/api/wells-fargo/wells-fargo-transaction';
import { Button } from '@/components/ui/button';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowUpDown } from 'lucide-react';

export const columns: ColumnDef<WellsFargoTransaction>[] = [
	{
		accessorKey: 'lockboxNum',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Lockbox #
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">
				{row.original.lockboxNum}
			</div>
		),
	},
	{
		accessorKey: 'lockboxDate',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Lockbox Date
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => {
			return (
				<div className="font-medium">
					{row.original.lockboxDate ? format(row.original.lockboxDate, 'P') : '---'}
				</div>
			);
		},
	},
	{
		accessorKey: 'batchNum',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Batch #
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">{row.original.batchNum}</div>
		),
	},
	{
		accessorKey: 'itemNum',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Item #
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">{row.original.itemNum}</div>
		),
	},
	{
		accessorKey: 'checkRemitterName',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Check Remitter Name
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">
				{row.original.checkRemitterName}
			</div>
		),
	},
	{
		accessorKey: 'checkNum',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Check #
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">{row.original.checkNum}</div>
		),
	},
	{
		accessorKey: 'checkAmount',
		header: ({ column }) => (
			<div className="text-right">
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					Check Amount
					<ArrowUpDown />
				</Button>
			</div>
		),
		cell: ({ row }) => {
			let formattedAmount;
			if (row.original.checkAmount) {
				formattedAmount = new Intl.NumberFormat('en-US', {
					style: 'currency',
					currency: 'USD',
				}).format(row.original.checkAmount);
			}
			return <div className="text-right font-medium">{formattedAmount}</div>;
		},
	},
	{
		accessorKey: 'inBalance',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Check In Balance
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="font-medium">{row.original.inBalance ? 'Yes' : 'No'}</div>
		),
	},
	{
		accessorKey: 'policyNum',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Policy #
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">{row.original.policyNum}</div>
		),
	},
	{
		accessorKey: 'invoiceNumber',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Invoice #
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">
				{row.original.invoiceNumber}
			</div>
		),
	},
	{
		accessorKey: 'velocityInvoiceAmount',
		header: ({ column }) => (
			<div className="text-right">
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					Velocity Invoice Amount
					<ArrowUpDown />
				</Button>
			</div>
		),
		cell: ({ row }) => {
			let formattedAmount = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format(row.original.velocityInvoiceAmount ?? 0);
			return <div className="text-right font-medium">{formattedAmount}</div>;
		},
	},
	{
		accessorKey: 'remittanceAmount',
		header: ({ column }) => (
			<div className="text-right">
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					Remittance Invoice Amount
					<ArrowUpDown />
				</Button>
			</div>
		),
		cell: ({ row }) => {
			let formattedAmount;
			if (row.original.remittanceAmount) {
				formattedAmount = new Intl.NumberFormat('en-US', {
					style: 'currency',
					currency: 'USD',
				}).format(row.original.remittanceAmount);
			}
			return <div className="text-right font-medium">{formattedAmount}</div>;
		},
	},
	{
		accessorKey: 'invoiceInBalance',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Invoice In Balance
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="font-medium">
				{row.original.invoiceInBalance ? 'Yes' : 'No'}
			</div>
		),
	},
	{
		accessorKey: 'isInVelocity',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Is In Velocity
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="font-medium">{row.original.isInVelocity ? 'Yes' : 'No'}</div>
		),
	},
	{
		accessorKey: 'largeOverPayment',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Large Over Payment
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="font-medium">
				{row.original.largeOverPayment ? 'Yes' : 'No'}
			</div>
		),
	},
	{
		accessorKey: 'minimumEarnedPremium',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Minimum Earned Premium
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="font-medium">
				{row.original.minimumEarnedPremium ? 'Yes' : 'No'}
			</div>
		),
	},
];
